import { ValidateJSModel } from 'forms/shared/types';
import { ForeignBornDocumentModel } from 'forms/shared/types';
import {
  citizenshipStatus, relationshipDerivedDocuments, relationshipNaturalizedDocuments,
  relationshipNonCitizenDocuments, relationshipUsForeignDocuments, residencyStatus
} from 'constants/enums/foreignBornDocumentCivlTypes';
import { DEFAULT_EARLIEST, DEFAULT_FUTURE_LATEST } from 'constants/dateLimits';
import { hasYesOrNo } from 'models/validate';

function isNationalOrNonCitizen(citizenshipType: string | null | undefined) {
  if (citizenshipType === null || citizenshipType === undefined) { return false; }
  return (
    citizenshipType === 'Not US'
    || citizenshipType === 'National By Birth In US'
    || citizenshipType === 'National By Birth Outside US'
  );
}

// only in pvq form so no need for check
const relationshipsSharedForeignBornDocumentCivilValidator: ValidateJSModel<ForeignBornDocumentModel> = {
  DocumentExpirationNotApplicable: {},
  DocumentNumberNotApplicable: {},
  NoCertificateNumber: {},
  Comments: {},

  CitizenType: {
    hasValue: {
      validator: {
        inclusion: {
          within: citizenshipStatus
        }
      },
    },
  },
  Expiration: {},

  DocumentType: (value, attributes) => {
    if (attributes?.CitizenType?.value === 'US Foreign') {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: relationshipUsForeignDocuments
            }
          },
        },
      };
    }
    if (attributes?.CitizenType?.value === 'Naturalized') {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: relationshipNaturalizedDocuments
            }
          },
        },
      };
    }
    if (attributes?.CitizenType?.value === 'Derived') {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: relationshipDerivedDocuments
            }
          },
        },
      };
    }
    if (isNationalOrNonCitizen(attributes?.CitizenType?.value) && attributes?.LivesInUnitedStates?.value === 'Yes') {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: relationshipNonCitizenDocuments
            }
          },
        },
      };
    }

    return {};
  },

  LivesInUnitedStates: (value, attributes) => {
    if (isNationalOrNonCitizen(attributes?.CitizenType?.value)) {
      return {
        presence: true, hasValue: { validator: hasYesOrNo }
      };
    }

    return {};
  },

  ResidenceType: (value, attributes) => {
    if (isNationalOrNonCitizen(attributes?.CitizenType?.value) && attributes?.LivesInUnitedStates?.value === 'Yes') {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: residencyStatus
            }
          },
        },
      };
    } return {};
  },

  ResidenceTypeOtherExplanation: (value, attributes) => {
    if (isNationalOrNonCitizen(attributes?.CitizenType?.value) && attributes?.LivesInUnitedStates?.value === 'Yes' && attributes?.ResidenceType?.value === 'Other') {
      return {
        hasValue: true,
      };
    } return {};
  },

  DocumentNumber: (value, attributes) => {
    if (attributes?.DocumentNumberNotApplicable?.applicable === false) {
      return {};
    }

    if (attributes?.CitizenType?.value === 'US Foreign') {
      if (attributes?.DocumentType?.value !== 'Other') {
        return {
          hasValue: true,
        };
      }
    }

    if (attributes?.CitizenType?.value === 'Naturalized') {
      if (attributes?.DocumentType?.value === 'NaturalizedCertificate'
        || attributes?.DocumentType?.value === 'NaturalizedPermanentAlien'
        || attributes?.DocumentType?.value === 'NaturalizedMriv') {
        return {
          hasValue: true,
        };
      }
    }

    if (attributes?.CitizenType?.value === 'Derived') {
      if (attributes?.DocumentType?.value === 'DerivedCertificate'
        || attributes?.DocumentType?.value === 'DerivedPermanentAlien'
        || attributes?.DocumentType?.value === 'DerivedMriv') {
        return {
          hasValue: true,
        };
      }
    }

    if (isNationalOrNonCitizen(attributes?.CitizenType?.value) && attributes?.LivesInUnitedStates?.value === 'Yes') {
      if ((attributes?.DocumentType?.value === 'NonPermanent'
        || attributes?.DocumentType?.value === 'NonEmployment'
        || attributes?.DocumentType?.value === 'NonMRIV'
        || attributes?.DocumentType?.value === 'NonArrival'
        || attributes?.DocumentType?.value === 'NonVisa'
        || attributes?.DocumentType?.value === 'NonF1'
        || attributes?.DocumentType?.value === 'NonJ1')) {
        return {
          hasValue: true,
        };
      }
    }

    return {};
  },

  CertificateNumber: (value, attributes) => {
    if (attributes?.NoCertificateNumber?.checked === true || attributes?.CertificateNumberNotApplicable?.applicable === false) {
      return {};
    }

    if (attributes?.CitizenType?.value === 'Naturalized') {
      if (attributes?.DocumentType?.value === 'NaturalizedCertificate') {
        return {
          hasValue: true,
        };
      }
    }
    if (attributes?.CitizenType?.value === 'Derived') {
      if ((attributes?.DocumentType?.value === 'DerivedCertificate')) {
        return {
          hasValue: true,
        };
      }
    }

    if (isNationalOrNonCitizen(attributes?.CitizenType?.value)) {
      if (attributes?.LivesInUnitedStates?.value === 'Yes' && attributes?.DocumentType?.value === 'Other') {
        return { hasValue: true };
      }

      return {};
    }

    if (attributes?.CitizenType?.value !== 'US birth') {
      if ((attributes?.DocumentType?.value === 'Other')) {
        return {
          hasValue: true,
        };
      }
    }

    return {};
  },

  OtherExplanation: (value, attributes) => {
    if (isNationalOrNonCitizen(attributes?.CitizenType?.value)) {
      if (attributes?.LivesInUnitedStates?.value === 'Yes' && attributes?.DocumentType?.value === 'Other') {
        return { hasValue: true };
      }

      return {};
    }

    if (attributes?.CitizenType?.value !== 'US birth') {
      if (attributes?.DocumentType?.value === 'Other') {
        return {
          hasValue: true,
        };
      }
    } return {};
  },

  DocumentExpiration: (value, attributes, attributeName, options) => {
    const { applicantBirthdate } = options;
    if (isNationalOrNonCitizen(attributes?.CitizenType?.value)) {
      if (
        attributes?.LivesInUnitedStates?.value === 'Yes'
        && attributes?.DocumentExpirationNotApplicable?.applicable !== false
        && attributes?.DocumentExpirationUnknown?.applicable !== false
      ) {
        return {
          presence: true,
          date: {
            earliest: applicantBirthdate || DEFAULT_EARLIEST,
            latest: DEFAULT_FUTURE_LATEST,
          }
        };
      }

      return {};
    }

    if (
      attributes?.CitizenType?.value !== 'US birth'
      && attributes?.DocumentExpirationNotApplicable?.applicable !== false
      && attributes?.DocumentExpirationUnknown?.applicable !== false
    ) {
      return {
        presence: true,
        date: {
          earliest: applicantBirthdate || DEFAULT_EARLIEST,
          latest: DEFAULT_FUTURE_LATEST,
        }
      };
    }

    return {};
  },
};

export default relationshipsSharedForeignBornDocumentCivilValidator;
